import React, { useCallback, useState, useRef } from 'react';
import { useMount, useUpdateEffect, usePrevious } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import * as apis from '@/apis';
import { Button, Popconfirm, Tooltip, Avatar, Tag, message } from 'antd';
import styles from './transactions.module.scss';
import PageableTable from '@/components/PageableTable';
import { orderPreview } from '@/models/orders/actions';
import { saveAs } from 'file-saver';

const tenantMode = process.env.APP_MODE === 'tenant';
function getColumns({ checkConfirm, settlementConfirm, dumpBills }) {
  let columns = 
   [
    {
      key: '_id',
      title: '',
      width: 30,
      render: (text, record, idx) => (
        <div style={{ whiteSpace: 'nowrap' }}>{idx + 1}.</div>
      ),
    },
    {
      title: '账单',
      render: (item) => (
        <Tooltip
          title={[item.title, item.paid_memo].filter(Boolean).join(' - ')}
        >
          {`${item.name}`}
        </Tooltip>
      ),
    },
    {
      title: '账单金额',
      render: (item) => (
        <div>
          <span>¥{item.total?.toFixed(2)}</span>
        </div>
      ),
    },
    {
      title: '交易数',
      render: (item) => item.transactions.length,
    },
    {
      title: '是否已核对',
      render: (item) =>
        item.isChecked ? (
          <span>已核对</span>
        ) : (
          <span style={{ color: '#f0ad15' }}>待核对</span>
        ),
    },
    {
      title: '是否已结算',
      render: (item) =>
        item.isPaid ? (
          <span>已结算</span>
        ) : (
          <span style={{ color: '#f0ad15' }}>未结算</span>
        ),
    },
    {
      title: '生成时间',
      render: (item) => dayjs(item.createdAt).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      render: (item) => {
        return (
          <div>
            {!tenantMode?(item.isChecked ? (
              !item.isPaid && (
                <Popconfirm
                  title="确认要结算?"
                  onConfirm={settlementConfirm(item._id)}
                  okText="确认"
                  cancelText="返回"
                >
                  <Button size="small" type="primary">
                    结算
                  </Button>
                </Popconfirm>
              )
            ) : (
              <Popconfirm
                title="确认已核对完成?"
                onConfirm={checkConfirm(item._id)}
                okText="确认"
                cancelText="返回"
              >
                <Button size="small" type="primary">
                  核对完成
                </Button>
              </Popconfirm>
            )):""}
            <Button size="small" type="primary" style={{marginLeft:5}} onClick={dumpBills(item._id)}>导出</Button>
          </div>
        );
      },
    },
  ];
//商家端隐藏操作列
  // return tenantMode?columns.slice(0,-1):columns
  return columns;
}

// 所有 交易记录
export default function BillList(props) {
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [list, setList] = useState([]);
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState({});
  const limit = 10;
  const dispatch = useDispatch();

  const handleRowClick = useCallback((item) => () => {
    dispatch(
      orderPreview({
        title: `工单 - ${item.order_no}`,
        id: item._id,
      })
    );
  });

  const paramsCache = useRef({});
  const pagination = {
    showTotal: (total) => `共计 ${total}条 记录`,
    showQuickJumper: true,
  };

  const fetch = useCallback((params = {}) => {
    setLoading(true);
    paramsCache.current = params;

    apis
      .getBills({ ...params, limit, tenant: props.tenantId })
      .then((result) => {
        setLoading(false);
        setTotal(result.total);
        setList(result.data);
      });
  }, []);

  const handleTableChange = useCallback(
    (params) => {
      window.scrollTop = 0;
      setOffset(params.offset);
      fetch({ ...filters, ...params });
    },
    [filters]
  );
  const dumpBills = useCallback(
    (id) => () => {
      apis
        .billDump(id)
        .then(res => {
          saveAs(res, `账单.csv`)
          // message.success('核对完成，账单已发送给商户');
        })
        .catch((err) => {
          message.error(err.message || '操作失败，请重试');
        });
    },
    []
  )
  const checkConfirm = useCallback(
    (id) => () => {
      apis
        .billConfirm(id)
        .then(() => {
          fetch();
          message.success('核对完成，账单已发送给商户');
        })
        .catch((err) => {
          message.error(err.message || '操作失败，请重试');
        });
    },
    []
  );
  const settlementConfirm = useCallback(
    (id) => () => {
      apis
        .billSettlement(id)
        .then(() => {
          fetch();
          message.success('已结算');
        })
        .catch((err) => {
          message.error(err.message || '操作失败，请重试');
        });
    },
    []
  );

  useMount(() => {
    fetch();
  });

  return (
    <div className={styles.wrap}>
      <PageableTable
        size="middle"
        className={styles.table}
        columns={getColumns({ checkConfirm, settlementConfirm, dumpBills })}
        pagination={pagination}
        total={total}
        limit={limit}
        offset={offset}
        dataSource={list}
        onChange={handleTableChange}
        expandedRowRender={(record, index, indent, expanded) => {
          if (!expanded) {
            return null;
          }
          return (
            <div className={styles.orders}>
              {record.orders.map((order, idx) => {
                return (
                  <div
                    className={styles.orderItem}
                    key={order._id}
                    onClick={handleRowClick(order)}
                  >
                    <div className={styles.row}>
                      {idx + 1}. {order.order_no}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        }}
        loading={loading}
      />
    </div>
  );
}
